// src/components/ContactUs.js
import { useState, useEffect } from "react";
import { Mail, Phone, Send } from "lucide-react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "../contexts/ToastContext";
import Header from "./Header";
import Footer from "./Footer";

const CONTENT = {
  ar: {
    docTitle: "تواصل معنا",
    subtitle: "تطبيق ترستفيكس — TrustFix",
    intro:
      "هل لديك سؤال أو ملاحظة؟ املأ النموذج أدناه وسيتواصل معك فريقنا في أقرب وقت.",
    infoTitle: "معلومات التواصل",
    emailLabel: "البريد الإلكتروني",
    phoneLabel: "الهاتف",
    fields: {
      name: "الاسم",
      email: "البريد الإلكتروني",
      phone: "رقم الهاتف",
      subject: "الموضوع",
      message: "الرسالة",
    },
    placeholders: {
      name: "اكتب اسمك",
      email: "example@email.com",
      phone: "055 000 0000",
      subject: "موضوع الرسالة",
      message: "اكتب رسالتك هنا...",
    },
    submit: "إرسال الرسالة",
    submitting: "جارٍ الإرسال...",
    success: "تم إرسال رسالتك بنجاح",
    error: "تعذّر إرسال الرسالة، حاول مرة أخرى.",
    required: "يرجى تعبئة الحقول المطلوبة.",
  },
  en: {
    docTitle: "Contact Us",
    subtitle: "TrustFix",
    intro:
      "Have a question or feedback? Fill in the form below and our team will get back to you shortly.",
    infoTitle: "Contact Information",
    emailLabel: "Email",
    phoneLabel: "Phone",
    fields: {
      name: "Name",
      email: "Email",
      phone: "Phone",
      subject: "Subject",
      message: "Message",
    },
    placeholders: {
      name: "Your name",
      email: "example@email.com",
      phone: "055 000 0000",
      subject: "Message subject",
      message: "Write your message here...",
    },
    submit: "Send Message",
    submitting: "Sending...",
    success: "Your message has been sent",
    error: "Could not send your message, please try again.",
    required: "Please fill in the required fields.",
  },
};

// Displayed contact details — edit to your real values
const CONTACT = {
  email: "admin@trustfixs.com",
  phone: "+966 54 432 7448",
};

const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };

export default function ContactUs() {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("tf_lang") || "ar";
    } catch {
      return "ar";
    }
  });

  const isAr = lang === "ar";
  const t = CONTENT[lang];

  const { showToast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem("tf_lang", lang);
    } catch {}
  }, [lang]);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      showToast(t.required);
      return;
    }

    setSending(true);
    try {
      await addDoc(collection(db, "contactMessages"), {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        status: "new",
        lang,
        createdAt: serverTimestamp(),
      });
      setForm(EMPTY);
      showToast(t.success);
    } catch (err) {
      console.error("Contact submit error:", err);
      showToast(t.error);
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]";

  return (
    <div className="min-h-screen bg-gray-50">
      <Header lang={lang} setLang={setLang} />
      <div className="min-h-screen" dir={isAr ? "rtl" : "ltr"}>
        <section>
          <div className="max-w-4xl mx-auto px-5 pt-3 pb-3">
            <h1 className="text-[26px] font-extrabold text-[#006C35] mt-2 mb-1">
              {t.docTitle}
            </h1>
            <p className="text-gray-500 text-[15px] mt-0 mb-6">{t.subtitle}</p>
          </div>
        </section>

        <section className="bg-white">
          <div className="max-w-4xl mx-auto px-5 pt-8 pb-12">
            <p className="text-gray-700 mb-8 leading-relaxed">{t.intro}</p>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Info blocks */}
              <div className="md:col-span-1 space-y-4">
                <h2 className="text-lg font-bold text-[#006C35] mb-2">
                  {t.infoTitle}
                </h2>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 bg-[#EDEEF2] rounded-lg flex items-center justify-center">
                    <Mail size={18} className="text-[#535C79]" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">{t.emailLabel}</div>
                    <a
                      href={`mailto:${CONTACT.email}`}
                      dir="ltr"
                      className="text-gray-800 hover:text-[#006C35] transition-colors"
                    >
                      {CONTACT.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 bg-[#EDEEF2] rounded-lg flex items-center justify-center">
                    <Phone size={18} className="text-[#535C79]" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">{t.phoneLabel}</div>
                    <a
                      href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                      dir="ltr"
                      className="text-gray-800 hover:text-[#006C35] transition-colors"
                    >
                      {CONTACT.phone}
                    </a>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="md:col-span-2">
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      {t.fields.name} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder={t.placeholders.name}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      {t.fields.email} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder={t.placeholders.email}
                      dir="ltr"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      {t.fields.phone}
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder={t.placeholders.phone}
                      dir="ltr"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      {t.fields.subject}
                    </label>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={update("subject")}
                      placeholder={t.placeholders.subject}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block text-sm text-gray-600 mb-1">
                    {t.fields.message} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder={t.placeholders.message}
                    className={`${inputClass} resize-y`}
                  />
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={sending}
                  className="inline-flex items-center gap-2 bg-[#006C35] text-white px-6 py-3 rounded-lg hover:bg-[#005428] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send size={18} />
                  {sending ? t.submitting : t.submit}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer lang={lang} />
    </div>
  );
}